import type { Banner, CasinoGame, Event, League, NavigationItem, Promotion, Sport } from '../types/domain';

export const navigation: NavigationItem[] = [
  { label: 'Top events', path: '/' },
  { label: 'Sports', path: '/sports' },
  { label: 'Live', path: '/live', badge: 'LIVE' },
  { label: 'Cricket', path: '/cricket' },
  { label: 'Esports', path: '/esports' },
  { label: 'Fast games', path: '/fast-games' },
  { label: 'Casino', path: '/casino' },
  { label: 'Live casino', path: '/live-casino' },
  { label: 'Promotions', path: '/promotions' },
];

export const sports: Sport[] = [
  { id: 'football', name: 'Football', eventCount: 526 },
  { id: 'cricket', name: 'Cricket', eventCount: 94 },
  { id: 'tennis', name: 'Tennis', eventCount: 318 },
  { id: 'basketball', name: 'Basketball', eventCount: 207 },
  { id: 'ice-hockey', name: 'Ice hockey', eventCount: 72 },
  { id: 'volleyball', name: 'Volleyball', eventCount: 118 },
  { id: 'table-tennis', name: 'Table tennis', eventCount: 164 },
  { id: 'esports', name: 'Esports', eventCount: 65 },
];

export const leagues: League[] = [
  { id: 'eng-premier', name: 'Premier League', country: { code: 'GB', name: 'England' }, sportId: 'football' },
  { id: 'esp-laliga', name: 'LaLiga', country: { code: 'ES', name: 'Spain' }, sportId: 'football' },
  { id: 'asia-cricket', name: 'Asia T20 Championship', country: { code: 'INT', name: 'International' }, sportId: 'cricket' },
  { id: 'atp-us', name: 'ATP Winston-Salem', country: { code: 'US', name: 'USA' }, sportId: 'tennis' },
  { id: 'esl', name: 'ESL Challenger', country: { code: 'INT', name: 'International' }, sportId: 'esports' },
];

const matchOdds = (id: string, values: [number, number, number]) => ({
  id: `${id}-1x2`,
  name: '1 X 2',
  odds: [
    { id: `${id}-w1`, label: '1', value: values[0] },
    { id: `${id}-x`, label: 'X', value: values[1] },
    { id: `${id}-w2`, label: '2', value: values[2] },
  ],
});

const totalOdds = (id: string, line: number, over: number, under: number) => ({
  id: `${id}-total`,
  name: 'Total',
  odds: [
    { id: `${id}-over`, label: `O ${line}`, value: over },
    { id: `${id}-under`, label: `U ${line}`, value: under },
  ],
});

export const events: Event[] = [
  { id: 'ars-liv', sportId: 'football', leagueId: 'eng-premier', homeTeam: 'Arsenal', awayTeam: 'Liverpool', startTime: '19:30', startDateLabel: 'Today', markets: [matchOdds('ars-liv', [2.45, 3.56, 2.78]), totalOdds('ars-liv', 2.5, 1.81, 2.02)], additionalMarketCount: 842, hasStats: true },
  { id: 'mci-che', sportId: 'football', leagueId: 'eng-premier', homeTeam: 'Manchester City', awayTeam: 'Chelsea', startTime: '22:00', startDateLabel: 'Today', markets: [matchOdds('mci-che', [1.54, 4.42, 5.80]), totalOdds('mci-che', 3.5, 2.17, 1.69)], additionalMarketCount: 715, hasStats: true },
  { id: 'sev-val', sportId: 'football', leagueId: 'esp-laliga', homeTeam: 'Sevilla', awayTeam: 'Valencia', startTime: '20:45', startDateLabel: 'Tomorrow', markets: [matchOdds('sev-val', [2.12, 3.24, 3.62]), totalOdds('sev-val', 2.5, 2.08, 1.76)], additionalMarketCount: 498 },
  { id: 'ind-sl', sportId: 'cricket', leagueId: 'asia-cricket', homeTeam: 'India', awayTeam: 'Sri Lanka', startTime: '16:00', startDateLabel: 'Today', markets: [matchOdds('ind-sl', [1.32, 7.50, 3.46]), totalOdds('ind-sl', 324.5, 1.88, 1.88)], additionalMarketCount: 126, hasStats: true },
  { id: 'fritz-ruud', sportId: 'tennis', leagueId: 'atp-us', homeTeam: 'T. Fritz', awayTeam: 'C. Ruud', startTime: '18:10', startDateLabel: 'Today', isLive: true, score: [1, 0], minute: 'Set 2', markets: [matchOdds('fritz-ruud', [1.42, 8.20, 2.88]), totalOdds('fritz-ruud', 22.5, 1.74, 2.05)], additionalMarketCount: 64, hasStats: true },
  { id: 'falcons-aurora', sportId: 'esports', leagueId: 'esl', homeTeam: 'Falcons', awayTeam: 'Aurora', startTime: '21:00', startDateLabel: 'Today', markets: [matchOdds('falcons-aurora', [1.67, 7.00, 2.14]), totalOdds('falcons-aurora', 2.5, 1.95, 1.80)], additionalMarketCount: 48, hasStats: true },
];

export const banners: Banner[] = [
  { id: 'all-sports', eyebrow: 'More markets. More moments.', title: 'Every game has a story', description: 'Explore top fixtures and competitive pre-match odds across the sports you follow.', cta: 'View top events', variant: 'hero' },
  { id: 'boost', eyebrow: 'Daily selection', title: 'A sharper way to follow the action', description: 'Build your slip from a curated lineup of today’s most watched events.', cta: 'Explore events', variant: 'boost' },
  { id: 'cricket', eyebrow: 'Cricket centre', title: 'From first ball to final over', description: 'Live score context and clear markets for the biggest fixtures.', cta: 'Open cricket', variant: 'cricket' },
];

export const promotions: Promotion[] = [
  { id: 'starter', eyebrow: 'New players', title: 'Welcome sports offer', description: 'Choose a sports welcome reward after registration. Terms and eligibility apply.', cta: 'View terms', tone: 'emerald' },
  { id: 'acca', eyebrow: 'Every weekend', title: 'Accumulator boost', description: 'Selected multi-event slips receive an enhanced return when all legs win.', cta: 'See eligible events', tone: 'red' },
  { id: 'casino', eyebrow: 'Casino spotlight', title: 'Weekly game missions', description: 'Complete featured play missions and unlock progress-based rewards.', cta: 'Explore missions', tone: 'gold' },
];

const gameSeed: CasinoGame[] = [
  { id: 'emerald-wheel', title: 'Emerald Wheel', provider: 'Northstar Studio', category: 'table', spritePosition: 'top-left', isHot: true },
  { id: 'golden-vault', title: 'Golden Vault', provider: 'Northstar Studio', category: 'slots', spritePosition: 'top-right', isNew: true },
  { id: 'neon-cards', title: 'Neon Cards', provider: 'Orbit Games', category: 'live', spritePosition: 'bottom-left' },
  { id: 'temple-crystal', title: 'Temple Crystal', provider: 'Wildlight', category: 'slots', spritePosition: 'bottom-right', isHot: true },
];

export const casinoGames: CasinoGame[] = Array.from({ length: 12 }, (_, index) => ({
  ...gameSeed[index % gameSeed.length],
  id: `${gameSeed[index % gameSeed.length].id}-${index}`,
  title: index < gameSeed.length ? gameSeed[index].title : `${gameSeed[index % gameSeed.length].title} ${Math.floor(index / 4) + 1}`,
  category: index % 6 === 4 ? 'fast' : index % 6 === 5 ? 'live' : gameSeed[index % gameSeed.length].category,
}));
